/**
 * profileContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

//import bluebird from "bluebird";
//import moment from "moment-timezone";
import * as React from "react";
import { Link, NavigateFunction, Route, Routes, useNavigate } from "react-router-dom";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Breadcrumbs, Button, CircularProgress, FormControl, IconButton, Tooltip, TextField } from "@mui/material";

import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { MfaSetupComponent } from "../auth/mfaSetup.component";

import { EMAIL_REGEX, ThirdPartyAuth } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";
import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";
import { DeviceListComponent } from "./deviceList.component";


export function ProfileContainerWrapper(): React.ReactElement<any, any> {
  const navigate = useNavigate();
  return <>
    <Routes>
      <Route path="/" element={<ProfileContainer navigate={navigate} />} />
      <Route path="/devices" element={<DeviceListComponent />} />
    </Routes>
  </>;
}

interface Props {
  navigate: NavigateFunction
}

interface State {
  sending: boolean,
  userID: string,
  firstname: string,
  lastname: string,
  emails: Array<string>,
  auths: Array<ThirdPartyAuth> | undefined,
  oldpass: string,
  pass: string,
  confirmpass: string,
  newEmail: string,
  errMsg: string,
  errors: {
    firstname: string,
    lastname: string,
    oldpass: string,
    pass: string,
    confirmpass: string,
    newEmail: string
  }
}

interface InputTimeouts {
  firstname: NodeJS.Timeout | null,
  lastname: NodeJS.Timeout | null
}

export class ProfileContainer extends React.Component<Props, State> {

  private lnameInput: HTMLInputElement | undefined;
  private passInput: HTMLInputElement | undefined;
  private confirmInput: HTMLInputElement | undefined;

  private timeouts: InputTimeouts;

  constructor(props: Props) {
    super(props);

    this.updateValue = this.updateValue.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.disablePassSubmit = this.disablePassSubmit.bind(this);
    this.changePass = this.changePass.bind(this);
    this.removeEmail = this.removeEmail.bind(this);
    this.addEmail = this.addEmail.bind(this);
    this.logout = this.logout.bind(this);

    this.timeouts = {
      firstname: null,
      lastname: null
    };

    this.state = {
      sending: false,
      userID: "",
      firstname: "",
      lastname: "",
      newEmail: "",
      emails: [],
      auths: undefined,
      oldpass: "",
      pass: "",
      confirmpass: "",
      errMsg: "",
      errors: {
        firstname: "",
        lastname: "",
        oldpass: "",
        pass: "",
        confirmpass: "",
        newEmail: ""
      }
    };
  }

  async componentDidMount(): Promise<void> {
    try {
      const authService = authServiceFactory();
      const user = await authService.getMe();

      this.setState({
        userID: user._id || "",
        firstname: user.firstname,
        lastname: user.lastname,
        emails: user.emails,
        auths: user.auths,
      });
    } catch (err) {
      console.error("Error fetching user:", err);
    }
  }

  updateValue(event: React.SyntheticEvent): void {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    let newValue = inputElem.value;
    switch (propName) {
      case "newEmail": newValue = newValue.toLowerCase(); break;
    }
    const update: any = { [propName]: newValue };
    this.setState(update);

    let msg = "";
    if (propName === "pass") {
      if (newValue === this.state.oldpass) {
        msg = "New password cannot match your old password";
      } else {
        msg = authServiceFactory().checkPWRequirements(newValue);
      }
    } else if (propName === "confirmpass") {
      if (!newValue) {
        return;
      } else if (newValue !== this.state.pass) {
        msg = "Password mismatch";
      }
    }
    this.setState({ errors: { ...this.state.errors, [propName]: msg } });
  }

  private onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    let propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    let msg = "";
    switch (propName) {
      case "firstname":
        if (!this.state.firstname) {
          msg = "Enter a first name."
        }
        break;
      case "lastname":
        if (!this.state.lastname) {
          msg = "Enter a last name."
        }
        break;
      case "email":
        propName = "emails";
        break;
      /*case "oldpass":
        if (!this.state.oldpass) {
          msg = "Please, enter your current password";
        }
        break;*/
      case "pass":
        if (!this.state.pass) {
          msg = "Please, choose a new password";
        } else {
          msg = authServiceFactory().checkPWRequirements(this.state.pass);
        }
        break;
      case "confirmpass":
        if (!this.state.confirmpass) {
          msg = "Please, re-enter new password";
        } else if (this.state.confirmpass !== this.state.pass) {
          msg = "Password mismatch";
        }
        break;
      case "newEmail":
        break;
      default:
        console.warn("Unknown input:", inputElem);
        return;
    }
    this.setState({ errors: { ...this.state.errors, [propName]: msg } });
  }

  private onKeyUp(event: React.KeyboardEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (event.key === "Enter") {
      switch (propName) {
        case "firstname":
          this.lnameInput?.focus();
          break;
        case "lastname":
          break;
        case "email":
          break;
        case "oldpass":
          this.passInput?.focus();
          break;
        case "pass":
          this.confirmInput?.focus();
          break;
        case "newEmail":
          this.addEmail();
          break;
        default:
          return;
      }
    } else if (inputElem.value && ["firstname", "lastname"].includes(propName)) {
      const timeout = this.timeouts[propName as keyof InputTimeouts];
      if (timeout) {
        clearTimeout(timeout);
      }
      this.timeouts[propName as keyof InputTimeouts] = setTimeout(async () => {
        try {
          console.log("saving:", propName, inputElem.value);
          await authServiceFactory().updateUserValue(propName, inputElem.value);
        } catch (err) {
          console.error("Error saving user value:", err);
          this.setState({ errMsg: (err as Error).message });
        }
      }, 1500);
    }
  }

  private disablePassSubmit(): boolean {
    return /*!this.state.oldpass ||*/ !this.state.pass || !this.state.confirmpass ||
      this.state.confirmpass !== this.state.pass;
  }

  private async changePass() {
    /*if (!this.state.oldpass) {
      this.setState({errors: {...this.state.errors, oldpass: "Current password required"}});
      return;
    }*/
    if (!this.state.pass) {
      this.setState({ errors: { ...this.state.errors, pass: "Please, enter a new password" } });
      return;
    }
    if (!this.state.confirmpass) {
      this.setState({ errors: { ...this.state.errors, confirmpass: "Please, re-enter new password" } });
      return;
    }
    if (this.state.confirmpass !== this.state.pass) {
      return; // Error should already be displayed
    }

    try {
      this.setState({ sending: true });
      await authServiceFactory().changePassword(this.state.oldpass, this.state.pass);
      this.setState({
        oldpass: "",
        pass: "",
        confirmpass: "",
        sending: false
      });
    } catch (err) {
      console.error("Error changing password:", err);
      this.setState({ errMsg: (err as Error).message });
    } finally {
      this.setState({ sending: false });
    }
  }

  private async removeEmail(index: number) {
    try {
      this.setState({ sending: true });
      await authServiceFactory().removeEmail(this.state.emails[index]);
      const newArr = Array.from(this.state.emails);
      newArr.splice(index, 1);
      this.setState({ emails: newArr });
    } catch (err) {
      console.error("Error removing email:", err);
      this.setState({ errMsg: (err as Error).message });
    } finally {
      this.setState({ sending: false });
    }
  }
  private async addEmail() {
    const email = this.state.newEmail.toLowerCase();
    if (!email) {
      this.setState({ errors: { ...this.state.errors, newEmail: "Please, enter an email address" } });
      return;
    }
    if (this.state.emails.includes(email)) {
      this.setState({ newEmail: "" });
      return;
    }
    if (!EMAIL_REGEX.test(this.state.newEmail)) {
      this.setState({ errors: { ...this.state.errors, newEmail: "Invalid Email Address" } });
      return;
    }
    try {
      this.setState({ sending: true });
      await authServiceFactory().addEmail(this.state.newEmail);
      const newArr = Array.from(this.state.emails);
      newArr.push(this.state.newEmail);
      this.setState({
        newEmail: "",
        emails: newArr
      });
    } catch (err) {
      console.error("Error adding email:", err);
      this.setState({ errMsg: (err as Error).message });
    } finally {
      this.setState({ sending: false });
    }
  }

  private logout() {
    try {
      authServiceFactory().logout();
      workerServiceFactory().didLogout();
      groupServiceFactory().didLogout();
      this.props.navigate("/login");
    } catch (err) {
      console.error("Error logging out:", err);
      this.setState({ errMsg: (err as Error).message });
    }
  }

  render(): React.ReactElement<any, any> {
    return <div className="profile-container main-section-container main-scroll-container">
      <div className="main-section-inner-with-header">
        <Breadcrumbs aria-label="breadcrumb" className="main-breadcrumbs">
          <Link to="/group/">
            <NavigateBeforeIcon fontSize="small" />
            <span>Back to Chat</span>
          </Link>
        </Breadcrumbs>
        <div className="profile-form-container">
          <form className="form-wrapper">
            <section>
              <h2>Personal Information</h2>
              <div className="form-row">
                <FormControl fullWidth>
                  <TextField id="firstname-input" label="First Name" name="firstname"
                    type="text" size="small"
                    error={!!this.state.errors.firstname}
                    helperText={this.state.errors.firstname}
                    variant="outlined"
                    value={this.state.firstname}
                    onChange={this.updateValue}
                    onBlur={this.onBlur}
                    onKeyUp={this.onKeyUp} />
                </FormControl>
              </div>
              <div className="form-row">
                <FormControl fullWidth>
                  <TextField id="lastname-input" label="Last Name" name="lastname"
                    type="text" size="small"
                    error={!!this.state.errors.lastname}
                    helperText={this.state.errors.lastname}
                    variant="outlined"
                    value={this.state.lastname}
                    inputRef={input => this.lnameInput = input}
                    onChange={this.updateValue}
                    onBlur={this.onBlur}
                    onKeyUp={this.onKeyUp} />
                </FormControl>
              </div>
              <h3>Email Addresses</h3>
              <div className="list-with-right-controls">
                {this.state.emails.map((email, index) =>
                  <div key={email} className="item-with-right-controls">

                    <div className="item-content">{email}</div>
                    <span className="right-controls">
                      <IconButton className="remove-btn" size="small"
                        disableRipple disableTouchRipple
                        onClick={() => this.removeEmail(index)}>
                        <Tooltip title="Remove Email" arrow>
                          <i className="fas fa-minus-circle"></i>
                        </Tooltip>
                      </IconButton>
                    </span>
                  </div>
                )}
                <div className="form-row">
                  <FormControl fullWidth>
                    <TextField id="email-input" label="Add Email" name="newEmail"
                      type="email" size="small"
                      error={!!this.state.errors.newEmail}
                      helperText={this.state.errors.newEmail}
                      variant="outlined"
                      value={this.state.newEmail}
                      onChange={this.updateValue}
                      onBlur={this.onBlur}
                      onKeyUp={this.onKeyUp} />
                  </FormControl>
                </div>
                <div className="button-row">
                  <Button
                    variant="contained"
                    disableRipple disableTouchRipple
                    onClick={this.addEmail}
                    disabled={!this.state.newEmail}>
                    {this.state.sending ? <CircularProgress size={20} /> : "Add"}
                  </Button>
                </div>
              </div>
            </section>

            <section className="change-pw-section">
              <h2>Change Password</h2>
              <div className="password-rules">
                <p>Passwords must:</p>
                <ul>
                  <li>be 10 characters or longer,</li>
                  <li>contain one lower case letter,</li>
                  <li>contain one capital letter,</li>
                  <li>contain one number,</li>
                  <li>contain one special character, or</li>
                  <li>be 24 characters or longer</li>
                </ul>
              </div>
              <div className="form-row">
                <FormControl fullWidth>
                  <TextField id="curpass-input" label="Current Password" name="oldpass"
                    type="password" size="small"
                    error={!!this.state.errors.oldpass}
                    helperText={this.state.errors.oldpass}
                    variant="outlined"
                    value={this.state.oldpass}
                    onChange={this.updateValue}
                    onBlur={this.onBlur}
                    onKeyUp={this.onKeyUp} />
                </FormControl>
              </div>
              <div className="form-row">
                <FormControl fullWidth>
                  <TextField id="pass-input" label="New Password" name="pass"
                    type="password" size="small"
                    required
                    error={!!this.state.errors.pass}
                    helperText={this.state.errors.pass}
                    variant="outlined"
                    value={this.state.pass}
                    inputRef={input => { this.passInput = input; }}
                    onChange={this.updateValue}
                    onBlur={this.onBlur}
                    onKeyUp={this.onKeyUp} />
                </FormControl>
              </div>
              <div className="form-row">
                <FormControl fullWidth>
                  <TextField id="confirm-input" label="Confirm New Password" name="confirmpass"
                    required
                    type="password" size="small"
                    error={!!this.state.errors.confirmpass}
                    helperText={this.state.errors.confirmpass}
                    variant="outlined"
                    value={this.state.confirmpass}
                    inputRef={input => { this.confirmInput = input; }}
                    onChange={this.updateValue}
                    onBlur={this.onBlur}
                    onKeyUp={this.onKeyUp} />
                </FormControl>
              </div>
              <div className="button-row">
                <Button
                  variant="contained"
                  disableRipple disableTouchRipple
                  onClick={this.changePass}
                  disabled={this.disablePassSubmit()}>
                  {this.state.sending ? <CircularProgress size={20} /> : "Change"}
                </Button>
              </div>
            </section>
            <section>
              <h2>My Devices</h2>
              <div className="button-row">
                <Button 
                  variant="contained"
                  disableRipple disableTouchRipple
                  onClick={() => this.props.navigate("/settings/devices")}>
                    Manage My Devices
                </Button>
              </div>
            </section>
            <section>
              <h2>MFA Devices</h2>
              <MfaSetupComponent
                userID={this.state.userID}
                email={this.state.emails[0]}
                showInternalAuth={true}
                auths={this.state.auths}
                didClearOTP={() => {/**/ }}
                didStartOTP={() => {/**/ }} />
            </section>
            <section>
              <h2>Misc</h2>
              <div className="center-button-row">
                <Button variant="contained"
                  disableRipple disableTouchRipple
                  onClick={this.logout}>
                  Logout
                </Button>
              </div>
            </section>
            <section className="version-info">
              <p>Copyright © {new Date().getFullYear()} Sentriqs, Inc.</p>
              <p>Version: {secrets.MARKETING_VERSION} ({secrets.DEPLOY_VERSION})</p>
            </section>
          </form>
        </div>
      </div>
      <ErrorSnackBar errMsg={this.state.errMsg} />
    </div>;
    /*<div>
            <Button color="secondary" variant="contained">Delete My Account...</Button>
          </div>*/
  }
}